import type { DocumentAssociation } from '@erp/rpc';

/**
 * Strip the wrapping `<>` some clients add and the `@host` suffix, so
 * `<image001.png@01D9.ABCD>` and the `cid:image001.png` reference in the
 * HTML body resolve to the same key.
 */
function normalizeContentId(contentId: string): string {
  const trimmed = contentId.trim().replace(/^<|>$/g, '');
  const atIndex = trimmed.indexOf('@');
  return atIndex === -1 ? trimmed : trimmed.substring(0, atIndex);
}

function addCidMapping(
  cidMap: Map<string, string>,
  key: string | null | undefined,
  url: string | null | undefined,
): void {
  if (!key || !url) return;
  const normalized = normalizeContentId(key);
  if (!normalized) return;
  // First writer wins so an explicit contentId is never overwritten by a
  // later attachment whose filename happens to collide with it.
  if (!cidMap.has(normalized)) cidMap.set(normalized, url);
}

/** Legacy attachment row; REST payloads use `originalName`, EmailData uses `fileName`. */
export interface CidMapAttachment {
  id?: string | null;
  contentId?: string | null;
  fileName?: string | null;
  originalName?: string | null;
  s3Url?: string | null;
}

export interface CidMapDocumentAssociation {
  document: {
    id?: string | null;
    contentId?: string | null;
    originalFileName?: string | null;
    name?: string | null;
    url?: string | null;
  };
}

/**
 * Map `cid:` references in an HTML body to signed URLs so inline images
 * render. Every source is keyed three ways — contentId, document/attachment
 * id and filename — because senders reference inline images inconsistently.
 * Structural parameter types so any email DTO fits without casting.
 */
export function buildCidMap({
  attachments,
  documentAssociations,
}: {
  attachments?: ReadonlyArray<CidMapAttachment> | null;
  documentAssociations?: ReadonlyArray<CidMapDocumentAssociation> | null;
} = {}): Map<string, string> {
  const cidMap = new Map<string, string>();

  for (const attachment of attachments ?? []) {
    addCidMapping(cidMap, attachment.contentId, attachment.s3Url);
    addCidMapping(cidMap, attachment.id, attachment.s3Url);
    addCidMapping(
      cidMap,
      attachment.fileName ?? attachment.originalName,
      attachment.s3Url,
    );
  }

  for (const { document } of documentAssociations ?? []) {
    addCidMapping(cidMap, document.contentId, document.url);
    addCidMapping(cidMap, document.id, document.url);
    addCidMapping(
      cidMap,
      document.originalFileName || document.name,
      document.url,
    );
  }

  return cidMap;
}

export interface NormalizedEmailAttachment {
  id: string;
  documentId?: string;
  fileName: string;
  sizeBytes: number;
  mimeType?: string;
  s3Url: string;
}

/**
 * Map document associations (oRPC thread shape) to the normalized
 * attachment item shape.
 */
export function documentAssociationsToAttachmentItems(
  documentAssociations?: DocumentAssociation[],
): NormalizedEmailAttachment[] {
  return (documentAssociations || []).map((doc) => ({
    id: doc.id,
    documentId: doc.document.id,
    fileName: doc.document.originalFileName || doc.document.name,
    sizeBytes: doc.document.fileSizeBytes || 0,
    mimeType: doc.document.mimeType || undefined,
    s3Url: doc.document.url,
  }));
}

/**
 * Map REST inbound-email attachments ({originalName, ...}) to the
 * normalized attachment item shape.
 */
export function restAttachmentsToAttachmentItems(
  attachments?: Array<{
    id: string;
    originalName: string;
    s3Url: string;
    mimeType: string;
    sizeBytes: number;
  }>,
): NormalizedEmailAttachment[] {
  return (attachments || []).map((att) => ({
    id: att.id,
    documentId: att.id, // REST attachments mapped from documents use the document id
    fileName: att.originalName,
    sizeBytes: att.sizeBytes,
    mimeType: att.mimeType,
    s3Url: att.s3Url,
  }));
}
